import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { api, ApiRequestError } from '../api/client';

type Purpose =
  | 'start_transformation'
  | 'request_assessment'
  | 'request_demo'
  | 'technical_discussion'
  | 'research_collaboration'
  | 'partnership'
  | 'client_support'
  | 'careers'
  | 'media';

const PURPOSES: Purpose[] = [
  'start_transformation',
  'request_assessment',
  'request_demo',
  'technical_discussion',
  'research_collaboration',
  'partnership',
  'client_support',
  'careers',
  'media',
];

// Field sets per purpose (spec §16 — "never one 20-field form for
// everyone"), matching backend/src/routes/leads.ts's zod schemas
// exactly so nothing submitted here can fail server-side validation
// for a reason the visitor wasn't shown.
const FIELD_SETS: Record<Purpose, string[]> = {
  start_transformation: ['organization', 'country', 'sector', 'size', 'role', 'challenge', 'transformation_goal', 'timeline'],
  request_assessment: ['organization', 'country', 'sector', 'role'],
  request_demo: ['organization', 'country', 'module', 'role'],
  technical_discussion: ['organization', 'country', 'sector', 'topic', 'role'],
  research_collaboration: ['institution', 'research_area', 'role'],
  partnership: ['organization', 'partnership_type', 'country'],
  client_support: ['organization'],
  careers: ['full_name', 'role_of_interest', 'portfolio'],
  media: ['organization', 'outlet', 'deadline'],
};

const FIELD_LABEL_KEY: Record<string, string> = {
  organization: 'field.organization',
  country: 'field.country',
  sector: 'field.sector',
  size: 'field.organization', // no distinct size label sourced — reuses organization's; see IMPLEMENTATION_CHECKLIST.md
  role: 'field.role',
  challenge: 'field.challenge',
  transformation_goal: 'field.challenge',
  timeline: 'field.timeline',
  module: 'field.module',
  topic: 'field.topic',
  institution: 'field.institution',
  research_area: 'field.research_area',
  partnership_type: 'field.partnership_type',
  full_name: 'field.full_name',
  role_of_interest: 'field.role_of_interest',
  portfolio: 'field.portfolio',
  outlet: 'field.outlet',
  deadline: 'field.deadline',
};

export default function Contact() {
  const locale = useLocale();
  const t = useT();
  const [searchParams] = useSearchParams();
  const [purpose, setPurpose] = useState<Purpose | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [consentProcessing, setConsentProcessing] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const p = searchParams.get('purpose') as Purpose | null;
    if (p && PURPOSES.includes(p)) setPurpose(p);
  }, [searchParams]);

  function setField(key: string, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!purpose) return;
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});

    const body: Record<string, unknown> = {
      purpose,
      contact: { name: contactName, email: contactEmail, ...(contactPhone ? { phone: contactPhone } : {}) },
      consent: { processing: consentProcessing, marketing: consentMarketing },
    };
    for (const key of FIELD_SETS[purpose]) {
      if (fields[key]) body[key] = fields[key];
    }
    // full_name/organization aliasing for purposes whose "organization"
    // field is conceptually the institution/full name — leads.ts on the
    // backend already falls back the same way.
    if (purpose === 'research_collaboration') body.organization = fields.institution;
    if (purpose === 'careers') body.organization = fields.full_name;

    try {
      await api.post('/api/v1/leads', body);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setSubmitError(err.message);
        const fe: Record<string, string> = {};
        err.fieldErrors?.forEach((f) => (fe[f.field] = f.message));
        setFieldErrors(fe);
      } else {
        setSubmitError('Something went wrong submitting this — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section className="section" style={{ paddingTop: 160, minHeight: '60vh' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1>{t('contact.confirm_headline')}</h1>
          <p className="body-lg">
            {t('contact.confirm_body_prefix')} {t(`contact.purpose.${purpose}`) || purpose} {t('contact.confirm_body_suffix')}
          </p>
          <Link className="btn btn-primary" to={withLocale('/', locale)}>
            {t('contact.return_home')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="section" style={{ paddingTop: 160 }}>
      <div className="container">
        <div className="section-head reveal is-visible">
          <span className="eyebrow">{t('contact.eyebrow')}</span>
          <h1>{t('contact.h1')}</h1>
          <p className="body-lg">{t('contact.lead')}</p>
        </div>

        {!purpose ? (
          <div className="card-grid">
            {PURPOSES.map((p) => (
              <button key={p} type="button" className="card reveal is-visible" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setPurpose(p)}>
                <h3>{humanizePurpose(p)}</h3>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={submit} className="card reveal is-visible" style={{ maxWidth: 640 }}>
            <button type="button" className="lisa-util-link" onClick={() => setPurpose(null)} style={{ marginBottom: 16 }}>
              {t('contact.change_purpose')}
            </button>

            {purpose === 'client_support' && (
              <p className="body-sm" style={{ marginBottom: 16 }}>
                {t('contact.client_support_note')}{' '}
                <Link to={withLocale('/portal-login', locale)}>{t('contact.client_support_link')}</Link> {t('contact.client_support_or')}
              </p>
            )}

            {FIELD_SETS[purpose].map((key) => (
              <div key={key} className={`field${fieldErrors[key] ? ' has-error' : ''}`}>
                <label htmlFor={key}>
                  {t(FIELD_LABEL_KEY[key] ?? key)}
                </label>
                <input id={key} type="text" value={fields[key] ?? ''} onChange={(e) => setField(key, e.target.value)} />
                {fieldErrors[key] && <p className="error-msg">{fieldErrors[key]}</p>}
              </div>
            ))}

            <div className="field">
              <label htmlFor="contactName">{t('field.full_name')}</label>
              <input id="contactName" required value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="contactEmail">Email</label>
              <input id="contactEmail" type="email" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="contactPhone">Phone (optional)</label>
              <input id="contactPhone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </div>

            <label className="body-sm" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
              <input type="checkbox" required checked={consentProcessing} onChange={(e) => setConsentProcessing(e.target.checked)} style={{ marginTop: 3 }} />
              <span>{t('contact.consent_processing')}</span>
            </label>
            <label className="body-sm" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 24 }}>
              <input type="checkbox" checked={consentMarketing} onChange={(e) => setConsentMarketing(e.target.checked)} style={{ marginTop: 3 }} />
              <span>{t('contact.consent_marketing')}</span>
            </label>

            {submitError && (
              <p className="body-sm" style={{ color: 'var(--status-critical)', marginBottom: 16 }}>
                {submitError}
              </p>
            )}

            <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
              {submitting ? '…' : t('contact.submit')}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function humanizePurpose(p: Purpose): string {
  return p
    .split('_')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}
